import { ArrowRight, BadgeCheck, Clock3, Droplets, Gauge, MapPin, Phone, Search, ShieldCheck, Star, Truck, Waves, Wrench } from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { company, services, allCities, reviews, faqs } from "@/lib/siteData";
import { directWhatsAppUrl } from "@/hooks/useWhatsAppMessage";

const icons = { Gauge, Truck, Waves, Droplets, Wrench, Search };

export const HomePage = () => (
  <main data-testid="homepage-main-content">
    <section className="promo-banner" data-testid="promo-banner">
      <a href={directWhatsAppUrl("promoção do banner")} aria-label="Promoção - Chamar no WhatsApp">
        <img
          className="banner-desktop"
          src="/img/Banner-Topo-Desktop.webp"
          alt="Promoção Desentupidora Ambiental - Desktop"
          loading="eager"
          fetchPriority="high"
          width="1200"
          height="300"
        />
        <img
          className="banner-mobile"
          src="/img/Banner-Topo-Celular.webp"
          alt="Promoção Desentupidora Ambiental - Mobile"
          loading="eager"
          fetchPriority="high"
          width="600"
          height="400"
        />
      </a>
    </section>

    <section className="hero-section" data-testid="hero-section">
      {/* Dynamic Glowing Mesh Background Orbs */}
      <div className="hero-glow-orbs" aria-hidden="true">
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
      </div>

      <div className="hero-content">
        <div className="hero-top-badge">
          <span className="live-pulse-dot"></span>
          <span>Plantão Ativo: Londrina e Região</span>
        </div>
        <h1 data-testid="hero-h1">
          Desentupidora em Londrina com <span className="highlight-gradient">solução técnica</span>, rápida e <span className="highlight-gradient">ambiental</span>.
        </h1>
        <p className="hero-copy" data-testid="hero-description">
          Hidrojateamento, limpa fossa, desentupimento de esgoto, pia, ralo, vaso sanitário e limpeza de caixa de gordura com equipe regularizada desde 2018.
        </p>
        <div className="hero-actions" data-testid="hero-primary-actions">
          <a className="primary-action" href={directWhatsAppUrl("desentupimento em Londrina")} data-testid="hero-whatsapp-cta-button">
            <Phone size={18} aria-hidden="true" /> Chamar no WhatsApp
          </a>
          <a className="secondary-action" href="#orcamento" data-testid="hero-budget-anchor-button">
            Solicitar orçamento <ArrowRight size={18} aria-hidden="true" />
          </a>
        </div>
      </div>

      <div className="hero-visuals">
        <div className="hero-glass-deck">
          <img
            className="hero-deck-bg"
            src="/img/escritorio.webp"
            alt="Escritório da MCS Desentupidora"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section className="metrics-band" data-testid="trust-metrics-section">
      {[
        [Clock3, "Atendimento 24 horas", "Emergência real para residências, comércios e condomínios"],
        [ShieldCheck, "Regularizada desde 2018", "CNPJ, inscrição municipal e dados públicos visíveis"],
        [BadgeCheck, "Serviço técnico", "Equipamentos para hidrojateamento, sucção e prevenção"],
      ].map(([Icon, title, text]) => (
        <article key={title} className="metric-item" data-testid={`trust-metric-${title.toLowerCase().replaceAll(" ", "-")}`}>
          <Icon size={24} aria-hidden="true" />
          <div>
            <strong>{title}</strong>
            <p>{text}</p>
          </div>
        </article>
      ))}
    </section>

    <section id="servicos" className="section-wrap" data-testid="services-section">
      <div className="section-heading" data-testid="services-heading">
        <p className="eyebrow">Serviços principais</p>
        <h2>Desentupimento, limpeza técnica e prevenção com foco em resposta rápida.</h2>
      </div>
      <div className="service-grid" data-testid="service-card-grid">
        {services.map((service) => {
          const Icon = icons[service.icon];
          return (
            <a className="service-card" href={`/servicos/${service.slug}.html`} key={service.slug} data-testid={`service-card-link-${service.slug}`}>
              {service.image && (
                <div className="service-image-wrapper">
                  <img src={service.image} alt={service.name} loading="lazy" width="400" height="220" decoding="async" />
                </div>
              )}
              <div className="service-content">
                <Icon size={26} aria-hidden="true" />
                <h3 data-testid={`service-title-${service.slug}`}>{service.name}</h3>
                <p data-testid={`service-copy-${service.slug}`}>{service.copy}</p>
                <span>Ver página SEO <ArrowRight size={16} aria-hidden="true" /></span>
              </div>
            </a>
          );
        })}
      </div>
    </section>

    <section id="cidades" className="coverage-section" data-testid="coverage-cities-section">
      <div>
        <p className="eyebrow">SEO regional e atendimento local</p>
        <h2 data-testid="coverage-heading">Cidades atendidas com páginas físicas para indexação.</h2>
        <p data-testid="coverage-description">A arquitetura inclui páginas HTML reais por cidade, serviço, bairro, blog e FAQ para fortalecer a presença orgânica da Desentupidora Ambiental no Google.</p>
      </div>
      <div className="city-cloud" data-testid="city-link-cloud">
        {allCities.map((city) => {
          const slug = city.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replaceAll(" ", "-");
          return (
            <a href={`/cidades/desentupidora-${slug}.html`} key={city} data-testid={`city-seo-link-${slug}`}>
              <MapPin size={15} aria-hidden="true" /> {city}
            </a>
          );
        })}
      </div>
    </section>

    <section id="avaliacoes" className="section-wrap" data-testid="reviews-section">
      <div className="section-heading" data-testid="reviews-heading">
        <p className="eyebrow">Prova social e EEAT</p>
        <h2>Avaliações destacando agilidade, suporte e confiança regional.</h2>
      </div>
      <div className="reviews-grid" data-testid="reviews-grid">
        {reviews.map((review, index) => (
          <article className="review-card" key={review.name} data-testid={`review-card-${index + 1}`}>
            <div className="stars" aria-label="5 estrelas" data-testid={`review-stars-${index + 1}`}><Star /><Star /><Star /><Star /><Star /></div>
            <p data-testid={`review-text-${index + 1}`}>“{review.text}”</p>
            <strong data-testid={`review-author-${index + 1}`}>{review.name}</strong>
            <span data-testid={`review-context-${index + 1}`}>{review.service} · {review.location}</span>
          </article>
        ))}
      </div>
    </section>

    <section className="faq-section" data-testid="homepage-faq-section">
      <div className="section-heading" data-testid="faq-heading">
        <p className="eyebrow">As pessoas também perguntam</p>
        <h2>Dúvidas frequentes sobre desentupidora em Londrina.</h2>
      </div>
      <div className="faq-list" data-testid="faq-list">
        {faqs.map((faq, index) => (
          <details key={faq.q} data-testid={`faq-item-${index + 1}`}>
            <summary data-testid={`faq-question-${index + 1}`}>{faq.q}</summary>
            <p data-testid={`faq-answer-${index + 1}`}>{faq.a}</p>
          </details>
        ))}
      </div>
    </section>

    <section id="orcamento" className="contact-section" data-testid="contact-budget-section">
      <div className="contact-left-visuals">
        <div className="contact-facade-container">
          <img
            className="contact-facade-img"
            src="/img/fachada.webp"
            alt="Fachada da MCS Desentupidora"
            loading="lazy"
          />
          <div className="contact-facade-caption">
            <span className="live-pulse-dot"></span>
            <span>Sede MCS Desentupidora em Londrina</span>
          </div>
        </div>
      </div>
      <div className="contact-right-content">
        <div className="contact-copy" data-testid="contact-copy-block">
          <p className="eyebrow">Conversão via WhatsApp</p>
          <h2>Descreva o problema e envie uma mensagem pronta para o atendimento.</h2>
          <p data-testid="contact-supporting-text">O formulário não cria barreira: ele organiza sua solicitação e abre o WhatsApp com todos os dados formatados para agilizar o retorno.</p>
          <ul data-testid="contact-benefits-list">
            <li>Atendimento emergencial 24 horas</li>
            <li>Serviços para casas, empresas, condomínios e indústrias</li>
            <li>Base em Londrina com atuação no Norte do Paraná</li>
          </ul>
        </div>
        <ContactForm />
      </div>
    </section>
  </main>
);